/**
 * dateRange.ts — timezone-correct day boundaries and <input type="date"> parsing.
 *
 * Every report screen previously rolled its own, using `Date#toISOString()` to
 * fill a date input and `new Date("2026-07-10")` to read it back. Both are UTC.
 * In IST (+05:30) that meant:
 *
 *   • before 05:30 local, "today" serialised as *yesterday*;
 *   • a range starting "2026-07-10" actually started at 05:30 IST, silently
 *     dropping the first 5½ hours of trading;
 *   • `new Date(to + 'T23:59:59')` has no `Z`, so it parsed as *local* — the two
 *     ends of one range used different timezone rules.
 *
 * Everything here is local-time by construction. Bounds are half-open
 * [start, end) so an event at exactly 23:59:59.999 cannot be double-counted or
 * dropped.
 */

/** Midnight at the start of `d`'s local day. */
export function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** Midnight at the start of the local day *after* `d`. */
export function startOfNextDay(d: Date): Date {
  const c = startOfDay(d);
  c.setDate(c.getDate() + 1);
  return c;
}

/**
 * Half-open millisecond bounds `[start, end)` covering the local day of `d`
 * (default: today). Suitable for `Q.gte(start)` + `Q.lt(end)`.
 */
export function dayBounds(d: Date = new Date()): [number, number] {
  return [startOfDay(d).getTime(), startOfNextDay(d).getTime()];
}

/**
 * Format a Date as the `yyyy-mm-dd` an `<input type="date">` expects, using
 * local calendar fields. Never use `toISOString().slice(0, 10)` for this.
 */
export function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse a `yyyy-mm-dd` input value as local midnight.
 * `new Date('2026-07-10')` would parse it as UTC midnight instead.
 */
export function fromDateInputValue(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/**
 * The last instant actually inside a half-open range ending at `toExclusive`.
 * Use for *display only* (headings, file names) — never as a query bound, or
 * you reintroduce the inclusive/exclusive mismatch this module exists to kill.
 */
export function lastIncludedInstant(toExclusive: Date): Date {
  return new Date(toExclusive.getTime() - 1);
}

export type RangePreset = 'today' | 'week' | 'month' | 'custom';

/**
 * Half-open bounds `[from, to)` for a preset. `custom` takes the two input
 * values and runs from the start of `from`'s day to the start of the day after
 * `to` — so the `to` day is fully included.
 */
export function presetRange(
  preset: RangePreset,
  custom?: { from: string; to: string },
): [Date, Date] {
  const now = new Date();
  switch (preset) {
    case 'today':
      return [startOfDay(now), startOfNextDay(now)];
    case 'week': {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 6);
      return [from, startOfNextDay(now)];
    }
    case 'month':
      return [new Date(now.getFullYear(), now.getMonth(), 1), startOfNextDay(now)];
    case 'custom': {
      if (!custom) return [startOfDay(now), startOfNextDay(now)];
      return [
        startOfDay(fromDateInputValue(custom.from)),
        startOfNextDay(fromDateInputValue(custom.to)),
      ];
    }
  }
}
